import React, { useState } from "react";
import styled from "@emotion/styled";
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Chip,
  Pagination,
  Skeleton,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CampaignIcon from "@mui/icons-material/Campaign";
import {
  useGetReleaseNotesQuery,
  useSetMaintenanceMessageMutation,
} from "api/homeApi";

const PAGE_SIZE = 10;

// Helper function to generate time options in 15-minute intervals between 7am and 9am
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 7; hour <= 9; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      if (hour === 9 && minute > 0) break; // stop after 9:00 AM
      const displayHour = hour > 12 ? hour - 12 : hour;
      const period = hour < 12 ? "AM" : "PM";
      const timeString = `${displayHour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")} ${period}`;
      const value = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      options.push({ label: timeString, value });
    }
  }
  return options;
};

const timeOptions = generateTimeOptions();

const PageTitle = styled(Typography)({
  fontFamily: "Montserrat",
  fontWeight: 700,
  fontSize: "20px",
  color: "#0a2240",
  marginBottom: "0.5rem",
});

const SectionTitle = styled(Typography)({
  fontFamily: "Montserrat",
  fontWeight: 600,
  fontSize: "1rem",
  color: "#0a2240",
  marginBottom: "1rem",
});

const StyledCard = styled(Card)({
  backgroundColor: "#ffffff",
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
});

const NoteRow = styled(Box)({
  padding: "12px 0",
  borderBottom: "1px solid #0000001A",
  "&:last-child": { borderBottom: "none" },
});

export default function ReleaseNotes() {
  const [page, setPage] = useState(1);
  const offset = (page - 1) * PAGE_SIZE;

  const { data: notesData, isFetching: loadingNotes } = useGetReleaseNotesQuery({
    limit: PAGE_SIZE,
    offset,
  });
  const [setMaintenanceMessage, { isLoading: submitting }] = useSetMaintenanceMessageMutation();

  const [formMessage, setFormMessage] = useState("");
  const [formDate, setFormDate] = useState("");
  const [maintenanceWindowStartTime, setMaintenanceWindowStartTime] = useState("07:00"); // Default 7am
  const [maintenanceWindowEndTime, setMaintenanceWindowEndTime] = useState("09:00"); // Default 9am
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const totalCount = notesData?.count ?? notesData?.length ?? 0;
  const rawNotes = notesData?.results ?? (Array.isArray(notesData) ? notesData : []);
  // Deduplicate by id in case the backend creates duplicate entries
  const notes = rawNotes.filter((note, idx, arr) => note.id == null || arr.findIndex((n) => n.id === note.id) === idx);
  const pageCount = Math.ceil(totalCount / PAGE_SIZE);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);
    if (!formMessage.trim() || !formDate) {
      setSubmitError("Both a message and a published date are required.");
      return;
    }
    if (!maintenanceWindowStartTime || !maintenanceWindowEndTime) {
      setSubmitError("Please select both a start time and end time for the maintenance window.");
      return;
    }
    try {
      await setMaintenanceMessage({
        message: formMessage.trim(),
        published_date: formDate,
        maintenance_start_time: maintenanceWindowStartTime,
        maintenance_end_time: maintenanceWindowEndTime,
      }).unwrap();
      setSubmitSuccess(true);
      setFormMessage("");
      setFormDate("");
      setMaintenanceWindowStartTime("07:00");
      setMaintenanceWindowEndTime("09:00");
    } catch (err) {
      setSubmitError(err?.data?.detail || err?.error || "Failed to save the release note.");
    }
  };

  return (
    <Box sx={{ padding: { xs: "1rem", sm: "1.5rem 1.5vw" }, maxWidth: "900px" }}>
      <PageTitle>Release Notes</PageTitle>
      <Typography sx={{ color: "#00000099", fontSize: "0.9rem", mb: 3 }}>
        Add release notes for upcoming maintenance windows and view past announcements. Posting a note
        for the current week will trigger the maintenance banner on the Dashboard.
      </Typography>

      {/* ── Add Note Form ── */}
      <StyledCard sx={{ mb: 4 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <CampaignIcon sx={{ color: "#005AA6" }} />
            <SectionTitle sx={{ mb: 0 }}>Add Release Note</SectionTitle>
          </Stack>
          <Typography sx={{ fontSize: "0.82rem", color: "#00000080", mb: 2 }}>
            Adding a note activates the maintenance banner on the Dashboard. Configure the maintenance window
            with your desired start and end times in 15-minute intervals.
          </Typography>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="Release Note"
              multiline
              minRows={3}
              fullWidth
              value={formMessage}
              onChange={(e) => setFormMessage(e.target.value)}
              placeholder="e.g. Scheduled maintenance — systems may be intermittently unavailable."
              sx={{ mb: 2 }}
              InputProps={{ sx: { fontFamily: "Montserrat", fontSize: "0.9rem" } }}
              InputLabelProps={{ sx: { fontFamily: "Montserrat" } }}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
              <TextField
                label="Published Date"
                type="date"
                value={formDate}
                onChange={(e) => setFormDate(e.target.value)}
                InputLabelProps={{ shrink: true, sx: { fontFamily: "Montserrat" } }}
                InputProps={{ sx: { fontFamily: "Montserrat", fontSize: "0.9rem" } }}
                sx={{ minWidth: 200 }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel sx={{ fontFamily: "Montserrat" }}>Maintenance Start Time (EST)</InputLabel>
                <Select
                  value={maintenanceWindowStartTime}
                  label="Maintenance Start Time (EST)"
                  onChange={(e) => setMaintenanceWindowStartTime(e.target.value)}
                  sx={{ fontFamily: "Montserrat", fontSize: "0.9rem" }}
                >
                  {timeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel sx={{ fontFamily: "Montserrat" }}>Maintenance End Time (EST)</InputLabel>
                <Select
                  value={maintenanceWindowEndTime}
                  label="Maintenance End Time (EST)"
                  onChange={(e) => setMaintenanceWindowEndTime(e.target.value)}
                  sx={{ fontFamily: "Montserrat", fontSize: "0.9rem" }}
                >
                  {timeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AddCircleOutlineIcon />}
                sx={{
                  background: "#0a2240",
                  fontFamily: "Montserrat",
                  fontWeight: 600,
                  textTransform: "none",
                  px: 3,
                  "&:hover": { background: "#005AA6" },
                }}
              >
                {submitting ? "Saving…" : "Save Note"}
              </Button>
            </Stack>
            {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
            {submitSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Release note saved with maintenance window from {maintenanceWindowStartTime} to {maintenanceWindowEndTime} EST.
                The maintenance banner will now appear on the Dashboard.
              </Alert>
            )}
          </Box>
        </CardContent>
      </StyledCard>

      {/* ── History ── */}
      <StyledCard>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <HistoryIcon sx={{ color: "#005AA6" }} />
            <SectionTitle sx={{ mb: 0 }}>Message History</SectionTitle>
          </Stack>

          {loadingNotes ? (
            <Stack spacing={1.5}>
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              ))}
            </Stack>
          ) : notes.length === 0 ? (
            <Typography sx={{ color: "#00000060", fontStyle: "italic", py: 2 }}>
              No archived messages yet.
            </Typography>
          ) : (
            <>
              {notes.map((note, idx) => (
                <NoteRow key={note.id ?? idx}>
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                    <Typography sx={{ fontFamily: "Montserrat", fontSize: "0.9rem", color: "#0a2240", flex: 1 }}>
                      {note.message}
                    </Typography>
                    <Chip
                      label={note.published_date}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: "Montserrat", fontWeight: 600, fontSize: "0.75rem", flexShrink: 0 }}
                    />
                  </Stack>
                  {note.archived_at && (
                    <Typography sx={{ fontSize: "0.78rem", color: "#00000060", mt: 0.5 }}>
                      Archived: {new Date(note.archived_at).toLocaleString()}
                    </Typography>
                  )}
                </NoteRow>
              ))}

              {pageCount > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Pagination
                    count={pageCount}
                    page={page}
                    onChange={(_, val) => setPage(val)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </StyledCard>
    </Box>
  );
}
